import { useCallback, useState } from 'react';
import { api } from './api.ts';

/**
 * A command against the API, with the three states a surface must show: idle,
 * running, and failed-with-a-reason.
 *
 * Commands are separated from reads deliberately. A read that fails can be
 * retried silently; a command that fails needs a person told what happened and
 * whether anything changed.
 */
export function useCommand<TResult>(): {
  run: (
    path: string,
    options: {
      method: 'POST' | 'PUT' | 'DELETE';
      body?: unknown;
      idempotencyKey?: string;
    },
  ) => Promise<TResult | null>;
  busy: boolean;
  error: unknown;
  clearError: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const run = useCallback(
    async (
      path: string,
      options: {
        method: 'POST' | 'PUT' | 'DELETE';
        body?: unknown;
        idempotencyKey?: string;
      },
    ): Promise<TResult | null> => {
      setBusy(true);
      setError(null);
      try {
        return await api.request<TResult>(path, {
          method: options.method,
          ...(options.body === undefined ? {} : { body: options.body }),
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
        });
      } catch (caught) {
        setError(caught);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return { run, busy, error, clearError: useCallback(() => setError(null), []) };
}
